import { Component, Fragment, Host, Prop, State, h } from "@stencil/core";
import { isEmpty } from "lodash-es";
import { getStore, ILiftStatusStore } from "../../store";
import { sendResetCommand, TResetCommand } from "../../utils/api";
import { watchStoreLocale } from "../../utils/locale-state";
import {
  applyErrorMessage,
  clearErrorMessage,
  getCompactErrorMessage,
} from "../../utils/error-message";
import { getStoreUnavailableError } from "../../utils/store-error";
import { ErrorType } from "../../utils/status";
import { ILiftStatusWebComponentError } from "../../utils/types";

const FEEDBACK_TIMEOUT_MS = 5000;

@Component({
  tag: "ls-reset-steering",
  styleUrl: "ls-reset-steering.css",
  shadow: true,
})
export class LsResetSteering {
  /**
   * The unique key that is used to identify store data.
   */
  @Prop() readonly idKey?: string;

  /**
   * Whether the reset controls are shown: the lift has the reset contact
   * capability and the user is an admin.
   */
  @State() isResetAvailable = false;

  /**
   * The command that is currently being sent, or `null` when idle.
   */
  @State() pendingCommand: TResetCommand | null = null;

  /**
   * Whether the inline confirmation for the force reset is shown.
   */
  @State() isConfirmingForce = false;

  /**
   * Feedback shown after a command completes.
   */
  @State() feedback: { kind: "success" | "error"; messageKey: string } | null =
    null;

  /**
   * Centralized error state for tracking component errors
   */
  @State() errorState: ILiftStatusWebComponentError = {
    hasError: false,
    messageKey: undefined,
    type: ErrorType.None,
  };

  private store: ILiftStatusStore | null = null;
  private storeUnsubscribe: (() => void) | null = null;
  private containerElement?: HTMLElement;
  private feedbackTimer: ReturnType<typeof setTimeout> | undefined;

  async componentWillLoad() {
    this.store = await getStore(this.idKey);
    watchStoreLocale(this, this.store, () => this.updateErrorState());

    this.isResetAvailable =
      Boolean(this.store.state.hasResetContactCapability) &&
      Boolean(this.store.state.isAdmin) &&
      !isEmpty(this.store.state.externalIdentifier);

    this.updateErrorState();

    const errorUnsubscribe = this.store.onChange("error", () => {
      this.updateErrorState();
    });
    this.storeUnsubscribe = () => {
      errorUnsubscribe();
    };
  }

  componentDidLoad() {
    // Container ref exists after first render; apply overlay without touching @State.
    this.applyErrorOverlay();
  }

  disconnectedCallback() {
    // Locale watcher cleanup is registered against this callback by watchStoreLocale.
    this.storeUnsubscribe?.();
    this.storeUnsubscribe = null;

    this.clearFeedbackTimer();

    if (this.containerElement !== undefined) {
      clearErrorMessage(this.containerElement);
    }
  }

  /**
   * Syncs the local error state from the store and (re)applies the overlay.
   */
  private updateErrorState() {
    if (this.store === null) {
      this.errorState = getStoreUnavailableError();
      this.applyErrorOverlay();
      return;
    }

    if (this.store.state.error.hasError) {
      this.errorState = {
        hasError: true,
        messageKey: this.store.state.error.messageKey,
        type: this.store.state.error.type,
      };
      this.applyErrorOverlay();
      return;
    }

    if (this.errorState.hasError) {
      this.errorState = {
        hasError: false,
        messageKey: undefined,
        type: ErrorType.None,
      };
    }

    this.applyErrorOverlay();
  }

  private applyErrorOverlay() {
    if (this.containerElement === undefined) {
      return;
    }

    if (this.errorState.hasError) {
      applyErrorMessage(
        this.containerElement,
        getCompactErrorMessage(
          this.errorState.messageKey,
          this.store?.state.locale,
        ),
      );
      return;
    }

    clearErrorMessage(this.containerElement);
  }

  private requestIdleReset = () => this.sendCommand("reset_contact_when_idle");

  private promptForceReset = () => {
    this.clearFeedback();
    this.isConfirmingForce = true;
  };

  private confirmForceReset = () => this.sendCommand("reset_contact");

  private cancelForceReset = () => {
    this.isConfirmingForce = false;
  };

  private sendCommand = async (command: TResetCommand) => {
    if (this.store === null || this.pendingCommand !== null) return;

    this.pendingCommand = command;
    this.clearFeedback();

    try {
      await sendResetCommand({ idKey: this.idKey, command });
      this.showFeedback("success", "ui:reset_steering.success");
    } catch (error) {
      this.logError(`Command ${command} failed`, error);
      this.showFeedback("error", "ui:reset_steering.error");
    } finally {
      this.pendingCommand = null;
      this.isConfirmingForce = false;
    }
  };

  /**
   * Shows command feedback and schedules it to auto-dismiss.
   */
  private showFeedback(kind: "success" | "error", messageKey: string) {
    this.clearFeedbackTimer();
    this.feedback = { kind, messageKey };
    this.feedbackTimer = setTimeout(() => {
      this.feedback = null;
      this.feedbackTimer = undefined;
    }, FEEDBACK_TIMEOUT_MS);
  }

  private clearFeedback() {
    this.clearFeedbackTimer();
    this.feedback = null;
  }

  private clearFeedbackTimer() {
    if (this.feedbackTimer !== undefined) {
      clearTimeout(this.feedbackTimer);
      this.feedbackTimer = undefined;
    }
  }

  private logError(message: string, error?: unknown) {
    console.group(
      `%cError%c [LsResetSteering] ${message}`,
      "color: #DC2626; background-color: #FEF2F2; display: inline-block; padding: 0 4px; border-radius: 2px;",
      "",
    );
    if (error !== undefined) {
      console.log(error instanceof Error ? error.message : String(error));
    }
    console.groupEnd();
  }

  private renderIdleReset(store: ILiftStatusStore) {
    const isSending = this.pendingCommand === "reset_contact_when_idle";
    return (
      <button
        type="button"
        class="reset-button idle"
        part="idle-button"
        disabled={this.pendingCommand !== null}
        onClick={this.requestIdleReset}
      >
        {isSending
          ? store.t("ui:reset_steering.sending")
          : store.t("ui:reset_steering.idle_label")}
      </button>
    );
  }

  private renderForceReset(store: ILiftStatusStore) {
    const isSending = this.pendingCommand === "reset_contact";

    if (this.isConfirmingForce) {
      return (
        <div class="confirm" part="confirm">
          <span class="confirm-question" part="confirm-question">
            {store.t("ui:reset_steering.confirm_question")}
          </span>
          <button
            type="button"
            class="reset-button force"
            part="confirm-button"
            disabled={this.pendingCommand !== null}
            onClick={this.confirmForceReset}
          >
            {isSending
              ? store.t("ui:reset_steering.sending")
              : store.t("ui:reset_steering.confirm")}
          </button>
          <button
            type="button"
            class="reset-button cancel"
            part="cancel-button"
            disabled={this.pendingCommand !== null}
            onClick={this.cancelForceReset}
          >
            {store.t("ui:reset_steering.cancel")}
          </button>
        </div>
      );
    }

    return (
      <button
        type="button"
        class="reset-button force"
        part="force-button"
        disabled={this.pendingCommand !== null}
        onClick={this.promptForceReset}
      >
        {store.t("ui:reset_steering.force_label")}
      </button>
    );
  }

  private renderControls(store: ILiftStatusStore) {
    return (
      <Fragment>
        <span class="title" part="title">
          {store.t("ui:reset_steering.title")}
        </span>
        <div class="buttons" part="buttons">
          {this.renderIdleReset(store)}
          {this.renderForceReset(store)}
        </div>
        {this.feedback !== null && (
          <span
            class={`feedback ${this.feedback.kind}`}
            part={`feedback ${this.feedback.kind}`}
            role="status"
          >
            {store.t(this.feedback.messageKey)}
          </span>
        )}
      </Fragment>
    );
  }

  private renderUnavailableNotice(store: ILiftStatusStore) {
    const messageKey = store.state.isAdmin
      ? "ui:reset_steering.no_capability"
      : "ui:reset_steering.no_permission";

    return (
      <p class="notice" part="notice" role="note">
        {store.t(messageKey)}
      </p>
    );
  }

  render() {
    const store = this.store;
    if (store === null) {
      return <Host aria-hidden="true"></Host>;
    }

    return (
      <Host>
        <div
          class="ls-reset-steering"
          part="container"
          ref={(el) => {
            this.containerElement = el;
          }}
        >
          {this.isResetAvailable || this.errorState.hasError
            ? this.renderControls(store)
            : this.renderUnavailableNotice(store)}
        </div>
      </Host>
    );
  }
}
